import _ from 'underscore';

import {AnalyticsExtensionsServerSectionSpec} from '~src/settings/general-settings/components/extensions-settings/analytics/AnalyticsExtensionsServerSection';
import {AnalyticsExtensionsSiteSectionSpec} from '~src/settings/general-settings/components/extensions-settings/analytics/AnalyticsExtensionsSiteSection';
import { PrepExtensionsSiteSectionSpecs } from '~src/settings/general-settings/components/extensions-settings/prep/PrepExtensionsSiteSection';
import { BridgeClientNotRunningNotificationsSectionSpecs } from '~src/settings/general-settings/components/sections/bridge/BridgeClientNotRunningNotificationsSection';
import { BridgePoolingSectionSpecs } from '~src/settings/general-settings/components/sections/bridge/BridgePoolingSection';
import { BridgeRemoteAgentsSectionSpecs } from '~src/settings/general-settings/components/sections/bridge/BridgeRemoteAgentsSection';
import { BridgeSettingsSummarySectionSpecs } from '~src/settings/general-settings/components/sections/bridge/BridgeSettingsSummarySection';

import { UpdateSiteLogoFormSpecs } from '../../siteLogo/forms/UpdateSiteLogoForm';
import { ExtensionsServerSectionSpecs } from './extensions-settings/dashboard/ExtensionsServerSection';
import { ExtensionsSiteSectionSpecs } from './extensions-settings/dashboard/ExtensionsSiteSection';
import { ActiveDirectorySyncSectionSpecs } from './sections/ActiveDirectorySyncSection';
import { AskDataSectionSpecs } from './sections/AskDataSection';
import { AutomaticAccessSectionSpecs } from './sections/AutomaticAccessSection';
import { AutoSuspendExtractRefreshSectionSpecs } from './sections/AutoSuspendExtractRefreshSection';
import { CacheWarmupSectionSpecs } from './sections/CacheWarmupSection';
import { CatalogingSectionSpecs } from './sections/CatalogingSection';
import { CollectionsSectionSpecs } from './sections/CollectionsSection';
import { CommentingSettingSectionSpecs } from './sections/CommentingSettingSection';
import { ConnectedClientsSettingSectionSpecs } from './sections/ConnectedClientsSettingSection';
import { CrossDataBaseJoinsSectionSpecs } from './sections/CrossDataBaseJoinsSection';
import { DataAccelerationSectionSpecs } from './sections/data-acceleration/DataAccelerationSection';
import { DataAlertsSectionSpecs } from './sections/DataAlertsSection';
import { DataQualitySubscriptionSectionSpec } from './sections/DataQualitySubscriptionSection';
import { EmailSettingsSpecs } from './sections/EmailSettingsSection';
import { EmbeddedCredentialsSectionSpecs } from './sections/EmbeddedCredentialsSection';
import { ExplainDataSectionSpecs } from './sections/ExplainDataSection';
import { ExtractEncryptionSectionSpecs } from './sections/ExtractEncryptionSection';
import { ServerGuestAccessSectionSpecs, SiteGuestAccessSectionSpecs } from './sections/GuestAccessSection';
import { LanguageAndLocaleSectionSpecs } from './sections/LanguageAndLocaleSection';
import { LinkedTaskServerSettingSpecs } from './sections/LinkedTaskServerSettingSection';
import { LinkedTaskSiteSettingSpecs } from './sections/LinkedTaskSiteSettingSection';
import { ManageUsersSectionSpecs } from './sections/manage-users-section/ManageUsersSection';
import { ManagedKeychainCleanupSectionSpecs } from './sections/ManagedKeychainCleanup';
import { MetricsContentTypeSpecs } from './sections/MetricsContentTypeSection';
import { MobileSectionSpecs } from './sections/MobileSection';
import { NotificationFlowRunsSpecs } from './sections/NotificationsFlowRunsSection';
import { NotifySiteAdminsOnThrottleSpec } from './sections/NotifySiteAdminsOnThrottleSection';
import { OAuthClientsRegistrySectionSpecs } from './sections/oauth-clients-registry/OAuthClientsRegistry';
import { PerformanceMetricsSectionSpecs } from './sections/PerformanceMetricsSection';
import { PersonalSpaceSectionSpecs } from './sections/personal-space-section/PersonalSpaceSection';
import { PrepConductorSpecs } from './sections/PrepConductorSection';
import { RecommendationsTrainingSectionSpecs } from './sections/RecommendationsTrainingSection';
import { RequestAccessSectionSpecs } from './sections/RequestAccessSection';
import { RevisionHistorySectionSpecs } from './sections/revision-history/RevisionHistorySection';
import { RunNowServerSettingSpec } from './sections/run-now/RunNowServerSettingSection';
import { RunNowSiteSettingSpec } from './sections/run-now/RunNowSiteSettingSection';
import { SavedCredentialsSpecs } from './sections/SavedCredentialsSection';
import { SensitiveLineageDataSectionSpecs } from './sections/SensitiveLineageDataSection';
import { SharingSectionSpecs } from './sections/SharingSection';
import { SignInNoteSpec } from './sections/SignInNoteSection';
import { SiteNameAndIdSpecs } from './sections/site-name-and-id/SiteNameAndIdSection';
import { SiteAvailabilitySpecs } from './sections/SiteAvailabilitySection';
import { SiteInviteNotificationSpecs } from './sections/SiteInviteNotificationSection';
import { SiteTimeZoneSectionSpecs } from './sections/SiteTimeZoneSection';
import { ServerStartPageSpecs, SiteStartPageSpecs } from './sections/StartPageSection';
import { StorageSectionSpecs } from './sections/storage/StorageSection';
import { SubscriptionsSpec } from './sections/SubscriptionsSection';
import { SupportAccessSectionSpecs } from './sections/SupportAccessSection';
import { TaggingSectionSpecs } from './sections/TaggingSection';
import { UserNotificationsSiteSettingsSectionSpecs } from './sections/UserNotificationsSiteSettingsSection';
import { UserVisibilitySpecs } from './sections/UserVisibilitySection';
import { VizRecommendationsSectionSpecs } from './sections/VizRecommendationsSection';
import { WebAuthoringSpecs } from './sections/WebAuthoringSection';
import { WebPageObjectsSectionSpecs } from './sections/WebPageObjectsSection';
import { ComponentSpecs } from './SettingsComponentRegistryFunctions';

export function createComponentRegistry(...specArray: ComponentSpecs<any>[]) {
  const result: _.Dictionary<ComponentSpecs<any>> = {};
  specArray.forEach(spec => result[spec.name] = spec);
  return result;
}

/**
 * All react-based sections in the settings page should be added to this object to allow them to be named in
 * the pug file while migrating, and to dynamically load them later on
 */
export const SettingsComponentRegistry = createComponentRegistry(
  ConnectedClientsSettingSectionSpecs,
  LanguageAndLocaleSectionSpecs,
  CrossDataBaseJoinsSectionSpecs,
  SupportAccessSectionSpecs,
  CollectionsSectionSpecs,
  CommentingSettingSectionSpecs,
  RequestAccessSectionSpecs,
  DataAlertsSectionSpecs,
  VizRecommendationsSectionSpecs,
  SharingSectionSpecs,
  WebPageObjectsSectionSpecs,
  AutomaticAccessSectionSpecs,
  SiteNameAndIdSpecs,
  SiteAvailabilitySpecs,
  PrepConductorSpecs,
  LinkedTaskSiteSettingSpecs,
  LinkedTaskServerSettingSpecs,
  WebAuthoringSpecs,
  StorageSectionSpecs,
  RevisionHistorySectionSpecs,
  ManageUsersSectionSpecs,
  ServerGuestAccessSectionSpecs,
  SiteGuestAccessSectionSpecs,
  PerformanceMetricsSectionSpecs,
  CatalogingSectionSpecs,
  TaggingSectionSpecs,
  CacheWarmupSectionSpecs,
  ManagedKeychainCleanupSectionSpecs,
  AutoSuspendExtractRefreshSectionSpecs,
  EmailSettingsSpecs,
  DataAccelerationSectionSpecs,
  SiteInviteNotificationSpecs,
  UserVisibilitySpecs,
  ServerStartPageSpecs,
  SiteStartPageSpecs,
  PersonalSpaceSectionSpecs,
  SubscriptionsSpec,
  NotificationFlowRunsSpecs,
  MobileSectionSpecs,
  NotifySiteAdminsOnThrottleSpec,
  AskDataSectionSpecs,
  ExplainDataSectionSpecs,
  MetricsContentTypeSpecs,
  SensitiveLineageDataSectionSpecs,
  RunNowServerSettingSpec,
  RunNowSiteSettingSpec,
  ExtractEncryptionSectionSpecs,
  UserNotificationsSiteSettingsSectionSpecs,
  SavedCredentialsSpecs,
  OAuthClientsRegistrySectionSpecs,
  ActiveDirectorySyncSectionSpecs,
  SiteTimeZoneSectionSpecs,
  SignInNoteSpec,
  UpdateSiteLogoFormSpecs,
  RecommendationsTrainingSectionSpecs,
  EmbeddedCredentialsSectionSpecs,
  BridgeSettingsSummarySectionSpecs,
  BridgeClientNotRunningNotificationsSectionSpecs,
  BridgePoolingSectionSpecs,
  BridgeRemoteAgentsSectionSpecs,
  ExtensionsServerSectionSpecs,
  ExtensionsSiteSectionSpecs,
  PrepExtensionsSiteSectionSpecs,
  AnalyticsExtensionsSiteSectionSpec,
  AnalyticsExtensionsServerSectionSpec,
  DataQualitySubscriptionSectionSpec
);
